<template>
	<div>
		<div v-if="tags?.length" class="tw:mb-3 tw:flex tw:flex-wrap tw:gap-2">
			<span v-for="t in tags" :key="t" class="tw:inline-flex tw:items-center tw:rounded-full tw:border tw:border-zinc-200 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium">
				{{ t }}
			</span>
		</div>
        <Title :title="title" />
		<div v-if="sanitizedDescription" class="tw:mb-4 tw:text-sm tw:text-zinc-600" >
			<div
				ref="wrapperRef"
				class="tw:relative tw:transition-[max-height] tw:duration-300 tw:ease-in-out"
				:style="descStyle"
			>
				<div ref="contentRef" v-html="sanitizedDescription"
				@click.stop="expand"></div>
				<div v-if="!expanded && canExpand" class="tw:pointer-events-none tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-12 tw:bg-gradient-to-t tw:from-white"></div>
			</div>
			<button
				v-if="canExpand"
				type="button"
				class="tw:mt-2 tw:text-sm tw:font-semibold tw:text-zinc-700 hover:tw:text-zinc-900"
				:aria-expanded="expanded.toString()"
				@click.stop="toggleExpand"
			>
				{{ expanded ? $t('Vis mindre') : $t('Les mer') }}
			</button>
		</div>
		<slot />
	</div>
</template>

<script setup>
	import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
	import Title from './title.vue'

	const props = defineProps({
		title: { type: String, required: true },
		description: { type: String, default: '' },
		tags: { type: Array, default: () => [] },
	})

	function sanitizeHtml(html) {
		if (!html || typeof window === 'undefined' || typeof DOMParser === 'undefined') return html || ''
		try {
			const parser = new DOMParser()
			const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html')
			const root = doc.body.firstElementChild
			if (!root) return ''

			const candidateTags = new Set(['p', 'div', 'span'])
			const isVisuallyEmpty = (el) => {
				const cleaned = el.innerHTML
					.replace(/&nbsp;/gi, '')
					.replace(/<br\s*\/?>/gi, '')
					.replace(/\s+/g, '')
					.trim()
				return cleaned.length === 0
			}

			const walk = (node) => {
				const children = Array.from(node.children)
				children.forEach(walk)
				const tag = node.tagName?.toLowerCase?.() || ''
				if (candidateTags.has(tag) && isVisuallyEmpty(node)) {
					node.remove()
				}
			}
			walk(root)

			// Trim leftover whitespace
			return root.innerHTML.trim()
		} catch (e) {
			return html
		}
	}

	const sanitizedDescription = computed(() => sanitizeHtml(props.description))

	// Read-more toggle logic
	const wrapperRef = ref(null)
	const contentRef = ref(null)
	const expanded = ref(false)
	const canExpand = ref(false)
	const contentHeight = ref(0)
	const CLAMP_PX = 100

	const measure = () => {
		const el = contentRef.value
		if (el) {
			contentHeight.value = el.scrollHeight || 0
			canExpand.value = contentHeight.value > CLAMP_PX
			// If content became small (e.g., filter change), reset expanded
			if (!canExpand.value) expanded.value = false
		}
	}

	const descStyle = computed(() => {
		if (!canExpand.value) return { maxHeight: 'none', overflow: 'visible' }
		const max = expanded.value ? `${contentHeight.value}px` : `${CLAMP_PX}px`
		return { maxHeight: max, overflow: 'hidden' }
	})

	const toggleExpand = () => {
		// Ensure we have an up-to-date content height before expanding
		if (!expanded.value) measure()
		expanded.value = !expanded.value
	}

    const expand = () => {
        expanded.value = true
    }

	onMounted(async () => {
		await nextTick()
		measure()
		window.addEventListener('resize', measure)
	})

	onBeforeUnmount(() => {
		window.removeEventListener('resize', measure)
	})

	watch(sanitizedDescription, async () => {
		await nextTick()
		measure()
	})
</script>

<style scoped>
</style>
